import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Mail, Loader2, Send, CheckCircle2, AlertTriangle, ExternalLink, Upload, Sparkles } from "lucide-react";

const DOC_TYPE_LABELS = {
  tax_invoice: "שליחת חשבונית מס",
  tax_invoice_receipt: "שליחת חשבונית מס / קבלה",
  receipt: "שליחת קבלה",
  credit_invoice: "שליחת חשבונית זיכוי",
  price_quote: "שליחת הצעת מחיר",
  transaction_account: "שליחת חשבון עסקה",
  work_order: "שליחת הזמנת עבודה",
  delivery_note: "שליחת תעודת משלוח",
  return_note: "שליחת תעודת החזרה",
  purchase_order: "שליחת הזמנת רכש",
};

// Short doc type name (without "שליחת" prefix) used inside email templates
const DOC_TYPE_SHORT = {
  tax_invoice: "חשבונית מס",
  tax_invoice_receipt: "חשבונית מס / קבלה",
  receipt: "קבלה",
  credit_invoice: "חשבונית זיכוי",
  price_quote: "הצעת מחיר",
  transaction_account: "חשבון עסקה",
  work_order: "הזמנת עבודה",
  delivery_note: "תעודת משלוח",
  return_note: "תעודת החזרה",
  purchase_order: "הזמנת רכש",
};

const defaultSubject = (shortLabel) => `${shortLabel} - {name}`;
const defaultBody = (shortLabel) => `שלום {name},\n\nמצורף ${shortLabel} על סך {amount}₪ עבור: {description}\n\nתודה רבה!`;
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { api } from "../lib/api";

export default function EmailDashboard() {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get("company");
  const docType = searchParams.get("doc") || "transaction_account";
  const docTypeLabel = DOC_TYPE_LABELS[docType] || "שליחת מסמך";
  const docTypeShort = DOC_TYPE_SHORT[docType] || "מסמך";
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState(null);
  const [gsheetUrl, setGsheetUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  // Track which docType each template was created for, so we can auto-refresh
  // the template text when user switches between doc types (unless they edited manually).
  const [subjectTemplate, setSubjectTemplate] = useState(defaultSubject(docTypeShort));
  const [bodyTemplate, setBodyTemplate] = useState(defaultBody(docTypeShort));
  const [templateDocType, setTemplateDocType] = useState(docType);
  const [templateEdited, setTemplateEdited] = useState(false);

  // When the URL doc type changes, refresh the default templates (unless user manually edited them).
  useEffect(() => {
    if (docType !== templateDocType) {
      if (!templateEdited) {
        setSubjectTemplate(defaultSubject(docTypeShort));
        setBodyTemplate(defaultBody(docTypeShort));
      }
      setTemplateDocType(docType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType]);

  const loadCompany = async () => {
    if (!companyId) { setLoading(false); return; }
    try {
      const { data } = await api.get("/companies");
      const c = (data.companies || []).find((c) => c.id === companyId);
      setCompany(c);
      if (c?.gsheet_url) setGsheetUrl(c.gsheet_url);
    } catch {
      setCompany(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCompany(); }, [companyId]);

  const handleLoadSheet = async () => {
    if (!gsheetUrl.trim()) {
      toast.error("יש להזין קישור Google Sheets");
      return;
    }
    setBatch(null);
    setResult(null);
    try {
      const { data } = await api.post("/batches/from-gsheet", { url: gsheetUrl.trim(), require_phone: false, company_id: companyId, doc_type: docType });
      // Backend returns { ok, batch_id, filename, customer_count, customers }
      // Normalize to { id, customers } for use here
      setBatch({
        id: data.batch_id || data.id,
        customers: data.customers || [],
        filename: data.filename,
      });
      toast.success(`נטענו ${data.customer_count || data.customers?.length || 0} לקוחות מהגיליון`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בטעינת הגיליון");
    }
  };

  const handleSendAll = async () => {
    if (!batch) {
      toast.error("יש לטעון גיליון קודם");
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const { data } = await api.post(`/companies/${companyId}/send-email-batch`, {
        company_id: companyId,
        batch_id: batch.id,
        subject_template: subjectTemplate,
        body_template: bodyTemplate,
        doc_type: docType,
      });
      setResult(data);
      toast.success(`נשלחו ${data.sent} מתוך ${data.total} אימיילים`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה בשליחה");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-brand" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-8" dir="rtl">
        <p className="text-stone-500">
          יש לבחור חברה מהסיידבר. <Link to="/S.ADMIN" className="text-brand underline">חזרה לדשבורד</Link>
        </p>
      </div>
    );
  }

  // Email provider — Brevo only (Resend removed)
  const hasEmailKey = !!company.brevo_api_key;
  const providerLabel = "Brevo";
  const providerDashboardUrl = "https://app.brevo.com/senders";
  const fromDisplay = company.brevo_from_email || company.email || "לא הוגדר";

  return (
    <div dir="rtl" className="p-6 sm:p-8 space-y-6 max-w-5xl mx-auto" data-testid="email-dashboard">
      <Toaster richColors position="top-center" />

      {/* Header - unified style with channel indicator */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="space-y-2">
          <p className="text-xs text-stone-500">{company.name}</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
            {docTypeLabel}
          </h1>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200" data-testid="channel-pill">
              <Mail className="h-3.5 w-3.5" />
              ערוץ שליחה: אימייל ({providerLabel})
            </span>
            <span className="text-xs text-stone-500">
              שליחה אוטומטית לכל לקוחות הגיליון בלחיצה אחת
            </span>
          </div>
        </div>
      </div>

      {/* Email provider status banner */}
      <div
        className={`rounded-xl p-4 flex items-center justify-between flex-wrap gap-3 border-2 ${
          hasEmailKey ? "bg-blue-50 border-blue-300" : "bg-amber-50 border-amber-300"
        }`}
        data-testid="email-provider-status"
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">{hasEmailKey ? "📧" : "⚠️"}</span>
          <div>
            <p className="font-bold text-sm">
              {hasEmailKey ? `שליחת אימייל פעילה (${providerLabel})` : `${providerLabel} לא מחובר`}
            </p>
            <p className="text-[11px] text-stone-600">
              שולח מ: <code dir="ltr" className="bg-white px-1.5 py-0.5 rounded font-mono text-[10px]">{fromDisplay}</code>
            </p>
          </div>
        </div>
        <a
          href={providerDashboardUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-bold px-3 py-1.5 rounded-md transition"
        >
          <ExternalLink className="h-3 w-3" />
          ניהול ב-{providerLabel}
        </a>
      </div>

      {/* Step 1: Load Google Sheet */}
      <div className="bg-white rounded-2xl border border-border p-5 space-y-3">
        <h3 className="font-heading font-bold text-lg">שלב 1: טעינת רשימת לקוחות</h3>
        <p className="text-xs text-stone-500 leading-relaxed">
          הזיני קישור ל-Google Sheets הכולל את העמודות: שם, אימייל, סכום, תיאור (ניתן גם טלפון).
        </p>

        {/* Visual guide - how the sheet should look */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-3 space-y-2" data-testid="sheet-guide">
          <p className="text-xs font-bold text-amber-900">📋 כך הגיליון שלך צריך להיראות:</p>
          <div className="overflow-x-auto bg-white rounded border border-amber-200">
            <table className="w-full text-xs" style={{minWidth: '500px'}}>
              <thead>
                <tr className="bg-amber-100">
                  <th className="border border-amber-200 p-2 text-amber-900 font-mono w-10">—</th>
                  <th className="border border-amber-200 p-2 text-amber-900 font-mono">A</th>
                  <th className="border border-amber-200 p-2 text-amber-900 font-mono">B</th>
                  <th className="border border-amber-200 p-2 text-amber-900 font-mono">C</th>
                  <th className="border border-amber-200 p-2 text-amber-900 font-mono">D</th>
                  <th className="border border-amber-200 p-2 text-amber-900 font-mono">E</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-blue-50">
                  <td className="border border-amber-200 p-2 font-mono text-stone-400 text-center">1</td>
                  <td className="border border-amber-200 p-2 font-bold text-stone-900">שם</td>
                  <td className="border border-amber-200 p-2 font-bold text-stone-900">אימייל</td>
                  <td className="border border-amber-200 p-2 font-bold text-stone-900">סכום</td>
                  <td className="border border-amber-200 p-2 font-bold text-stone-900">תיאור</td>
                  <td className="border border-amber-200 p-2 font-bold text-stone-400">טלפון (רשות)</td>
                </tr>
                <tr>
                  <td className="border border-amber-200 p-2 font-mono text-stone-400 text-center">2</td>
                  <td className="border border-amber-200 p-2">דני כהן</td>
                  <td className="border border-amber-200 p-2 text-right" dir="ltr">dani@gmail.com</td>
                  <td className="border border-amber-200 p-2">1500</td>
                  <td className="border border-amber-200 p-2">ייעוץ מאי</td>
                  <td className="border border-amber-200 p-2" dir="ltr">050-1234567</td>
                </tr>
                <tr>
                  <td className="border border-amber-200 p-2 font-mono text-stone-400 text-center">3</td>
                  <td className="border border-amber-200 p-2">שרה לוי</td>
                  <td className="border border-amber-200 p-2 text-right" dir="ltr">sara@gmail.com</td>
                  <td className="border border-amber-200 p-2">2000</td>
                  <td className="border border-amber-200 p-2">שיעור פרטי</td>
                  <td className="border border-amber-200 p-2" dir="ltr">053-7654321</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="text-[11px] text-amber-900 space-y-0.5 leading-relaxed">
            <p>✅ <strong>כותרות מזוהות בעברית:</strong> שם, אימייל, סכום / מחיר, תיאור / שירות, טלפון</p>
            <p>✅ <strong>גם באנגלית:</strong> name, email, amount, description, phone</p>
            <p>⚠️ <strong>חובה:</strong> שורה 1 = כותרות, שורה 2+ = נתונים</p>
            <p>⚠️ <strong>שיתוף:</strong> "שתף" ← "כל מי שיש לו הקישור" ← "צופה"</p>
          </div>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Input
            dir="ltr"
            value={gsheetUrl}
            onChange={(e) => setGsheetUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/..."
            className="flex-1 text-right min-w-[250px]"
            data-testid="gsheet-url-input"
          />
          <Button onClick={handleLoadSheet} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="load-sheet-btn">
            <Upload className="h-4 w-4 ml-2" />
            טען גיליון
          </Button>
        </div>
      </div>

      {/* Step 2: Preview - full excel-like table */}
      {batch && (
        <>
          <div className="bg-white rounded-2xl border border-border p-5 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-heading font-bold text-lg">שלב 2: תצוגה מלאה של הגיליון ({batch.customers?.length || 0} לקוחות)</h3>
                <p className="text-xs text-stone-500 mt-0.5">
                  זה בדיוק מה שנטען מה-Google Sheets שלך:
                </p>
              </div>
            </div>

            {/* Excel-like scrollable table */}
            <div className="border-2 border-stone-300 rounded-lg overflow-hidden" style={{maxHeight: '400px', overflow: 'auto'}} data-testid="excel-preview">
              <table className="w-full text-sm border-collapse" style={{minWidth: '700px'}}>
                <thead className="sticky top-0 z-10">
                  <tr className="bg-stone-200">
                    <th className="border border-stone-300 p-2 font-mono text-xs text-stone-600 w-10 text-center bg-stone-300">—</th>
                    <th className="border border-stone-300 p-2 font-mono text-xs text-stone-700 bg-stone-200">A<br/><span className="text-[10px] text-stone-500">שם</span></th>
                    <th className="border border-stone-300 p-2 font-mono text-xs text-stone-700 bg-stone-200">B<br/><span className="text-[10px] text-stone-500">אימייל</span></th>
                    <th className="border border-stone-300 p-2 font-mono text-xs text-stone-700 bg-stone-200">C<br/><span className="text-[10px] text-stone-500">סכום</span></th>
                    <th className="border border-stone-300 p-2 font-mono text-xs text-stone-700 bg-stone-200">D<br/><span className="text-[10px] text-stone-500">תיאור</span></th>
                    <th className="border border-stone-300 p-2 font-mono text-xs text-stone-700 bg-stone-200">E<br/><span className="text-[10px] text-stone-500">טלפון</span></th>
                  </tr>
                </thead>
                <tbody>
                  {(batch.customers || []).map((c, i) => {
                    const hasEmail = !!c.email && c.email.includes("@");
                    return (
                      <tr key={i} className={hasEmail ? "hover:bg-blue-50" : "hover:bg-red-50 bg-red-50/30"} data-testid={`customer-row-${i}`}>
                        <td className="border border-stone-300 p-2 font-mono text-xs text-center bg-stone-100 text-stone-500 font-bold">{i + 2}</td>
                        <td className="border border-stone-300 p-2 font-semibold">{c.name || <span className="text-stone-300 italic">ריק</span>}</td>
                        <td className={`border border-stone-300 p-2 font-mono text-xs ${hasEmail ? "text-green-700" : "text-red-600 font-bold"}`} dir="ltr">
                          {hasEmail ? c.email : "⚠️ חסר"}
                        </td>
                        <td className="border border-stone-300 p-2 text-right">₪{parseFloat(c.amount || 0).toFixed(2)}</td>
                        <td className="border border-stone-300 p-2 text-stone-700">{c.description || <span className="text-stone-300 italic">ריק</span>}</td>
                        <td className="border border-stone-300 p-2 font-mono text-xs text-stone-600" dir="ltr">{c.phone_e164 || c.phone_raw || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Summary stats */}
            <div className="grid grid-cols-3 gap-3 pt-2">
              <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-green-700">
                  {(batch.customers || []).filter((c) => c.email && c.email.includes("@")).length}
                </p>
                <p className="text-[10px] text-green-800">יישלחו אליהם מייל</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-red-700">
                  {(batch.customers || []).filter((c) => !c.email || !c.email.includes("@")).length}
                </p>
                <p className="text-[10px] text-red-800">ללא אימייל - יידלגו</p>
              </div>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-stone-700">{batch.customers?.length || 0}</p>
                <p className="text-[10px] text-stone-700">סה״כ בגיליון</p>
              </div>
            </div>

            {(batch.customers || []).some((c) => !c.email || !c.email.includes("@")) && (
              <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3 space-y-1" data-testid="missing-emails-warning">
                <p className="text-sm font-bold text-red-900 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  חסרים אימיילים בחלק מהשורות!
                </p>
                <p className="text-[11px] text-red-800 leading-relaxed">
                  השורות המסומנות בוורוד למעלה לא יקבלו מייל. הוסיפי להן כתובת בעמודת ה"אימייל" בגיליון וטעני שוב.
                </p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-border p-5 space-y-3">
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-purple-600" />
              שלב 3: תבנית ההודעה
            </h3>
            <p className="text-xs text-stone-500 leading-relaxed">
              ניתן להשתמש במשתנים: <code className="bg-stone-100 px-1 rounded">{"{name}"}</code>, <code className="bg-stone-100 px-1 rounded">{"{amount}"}</code>, <code className="bg-stone-100 px-1 rounded">{"{description}"}</code>
            </p>
            <div>
              <Label>נושא האימייל</Label>
              <Input
                value={subjectTemplate}
                onChange={(e) => { setSubjectTemplate(e.target.value); setTemplateEdited(true); }}
                className="mt-1"
                data-testid="subject-input"
              />
            </div>
            <div>
              <Label>גוף ההודעה</Label>
              <Textarea
                value={bodyTemplate}
                onChange={(e) => { setBodyTemplate(e.target.value); setTemplateEdited(true); }}
                rows={5}
                className="mt-1"
                data-testid="body-input"
              />
            </div>
          </div>

          {/* Step 3: Send */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl border-2 border-blue-200 p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="font-heading font-bold text-lg">מוכנה לשליחה?</h3>
              <p className="text-xs text-stone-600">
                האימיילים יישלחו מ <strong>{fromDisplay}</strong> לכל הלקוחות ברשימה.
              </p>
            </div>
            <Button
              onClick={handleSendAll}
              disabled={sending || !hasEmailKey}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-md"
              data-testid="send-all-btn"
            >
              {sending ? (
                <><Loader2 className="h-5 w-5 ml-2 animate-spin" />שולח...</>
              ) : (
                <><Send className="h-5 w-5 ml-2" />שלחי לכל הלקוחות</>
              )}
            </Button>
          </div>
        </>
      )}

      {/* Result */}
      {result && (
        <div className="bg-white rounded-2xl border border-border p-5" data-testid="send-result">
          <h3 className="font-heading font-bold text-lg mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            תוצאות השליחה
          </h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{result.sent}</p>
              <p className="text-xs text-green-800">נשלחו בהצלחה</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-red-700">{result.failed}</p>
              <p className="text-xs text-red-800">נכשלו</p>
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-stone-700">{result.total}</p>
              <p className="text-xs text-stone-800">סה״כ</p>
            </div>
          </div>
          {/* GI-level warning if any invoice failed */}
          {(result.details || []).some((d) => d.invoice_error) && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-3" data-testid="invoice-error-banner">
              <p className="text-sm font-bold text-amber-900 flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4" />
                חשבוניות ירוקות לא נוצרו
              </p>
              <p className="text-xs text-amber-800 leading-relaxed">
                האימיילים נשלחו — אבל חשבונית ירוקה החזירה שגיאה ולכן המסמך לא צורף.
                {" "}
                {(result.details || []).find((d) => d.invoice_error && d.invoice_error.includes("1112"))
                  ? "הסיבה: חשבון חשבונית ירוקה הגיע למכסת המסמכים החודשית. יש לשדרג את החבילה או להמתין לחודש הבא."
                  : "בדקי את המפתחות של חשבונית ירוקה בהגדרות החברה."}
              </p>
            </div>
          )}
          <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
            {(result.details || []).map((d, i) => (
              <div key={i} className={`text-sm p-2.5 rounded-lg ${d.ok ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`} data-testid={`result-row-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-semibold">
                    {d.ok ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-red-600" />}
                    {d.name}
                  </span>
                  <span className="text-xs text-stone-600" dir="ltr">{d.email || d.error}</span>
                </div>
                {d.invoice_url && (
                  <a href={d.invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-1 text-xs text-green-700 hover:underline font-semibold">
                    <ExternalLink className="h-3 w-3" />
                    חשבון עסקה מס׳ {d.invoice_number}
                  </a>
                )}
                {d.invoice_error && !d.invoice_url && (
                  <p className="text-[11px] text-amber-800 mt-1 leading-snug">
                    ⚠️ חשבונית ירוקה: {d.invoice_error.includes("1112") ? "הגעת למכסת המסמכים החודשית בחשבון" : d.invoice_error.replace(/HTTP \d+: /, '').slice(0, 150)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
